import React, { useEffect, useState } from 'react'
import { CircularProgress, Drawer, IconButton, Tooltip } from '@mui/material';
import { Assignment, Close, HistoryTwoTone, InfoRounded } from '@mui/icons-material';
import TaskProgressGauge from './editor/TaskProgressGuage';
import taskServices from '../taskServices';
import { useAlert } from '../../../../../context/AlertContext';
import TaskActivityList from './TaskActivityList';
import TaskStatusBadge from './editor/TaskStatusBadge';
import PriorityBadge from './editor/PriorityBadge';
import AssigneeBadge from './editor/AssigneeBadge';
import TaskRemarkUpdate from './editor/TaskRemarkUpdate';
import TaskScheduleBadge from './editor/TaskScheduleBadge';
import TaskBasicInfoUpdate from './editor/TaskBasicInfoUpdate';
import { useAppData } from '../../../../../context/AppDataContext';
import PunchLocationBadge from './editor/PunchLocationBadge';
import ContributorBadge from './editor/ContributorBadge';
import { useAuth } from '../../../../../context/AuthContext';
import ConfirmDialog from '../../../../../components/ConfirmDialog';


function TaskDetailsView({taskDetails,setTaskDetails,callBack}) {    

    const [saving,setSavingStatus] = useState(false);
    const {AppAlert} = useAlert();
    const {getMasterLabel,getDDDataById,userDD} = useAppData();
    const {authUser } = useAuth();
    const [isCreator,setIsCreator] = useState(false);
    const [isAssignee,setIsAssignee] = useState(false)

    useEffect(()=>{
        if(taskDetails){
            if(taskDetails.userId == authUser.id){
                setIsCreator(true);
            }
            if(taskDetails.assignedTo == authUser.id){
                setIsAssignee(true);
            }
        }
        return(()=>{
            setIsCreator(false);
            setIsAssignee(false);
        })
    },[taskDetails])

    const updateTask = (payload) =>{
        payload.data.sequence = Number(taskDetails.sequence) + 1;
        setSavingStatus(true);
        taskServices.update(payload).then(res=>{
            AppAlert(res.msg);
            callBack();
        }).catch(error=>{
            AppAlert(error.msg,"error");
        }).finally(()=>{
            setSavingStatus(false);
        })
    }

    const updateProgress = (progressPayload) => {
        // 1. Build the activity log entry
        const activity = {
            taskId: taskDetails.id,
            activityType: "progress_update", 
            oldValue: taskDetails.progress,
            newValue: progressPayload.progress,
            remark: progressPayload.remark || null // Good to include if user leaves a note
        };
        // 2. Build the actual data update
        const data = {
            id: taskDetails.id,
            progress: progressPayload.progress,
            notify: progressPayload.notify
        };
        // 3. Wrap into a clean payload
        const finalPayload = {
            data,
            activity // Simplified ES6 syntax (activity: activity)
        };
        updateTask(finalPayload);
    };

    const updateStatus = (statusPayload) => {
        const activity = {
            taskId: taskDetails.id,
            activityType: "status_change", 
            oldValue: taskDetails.taskStatus, // Make sure taskDetails has status
            newValue: statusPayload.taskStatus,
            remark: statusPayload.remark || null
        };

        const data = {
            id: taskDetails.id,
            taskStatus: statusPayload.taskStatus,
            notify: statusPayload.notify
        };

        updateTask({ data, activity });
    };


    const updatePriority = (priorityPayload) => {
        const activity = {
            taskId: taskDetails.id,
            activityType: "priority_change", 
            oldValue: taskDetails.priority,
            newValue: priorityPayload.priority,
            remark: priorityPayload.remark || null
        };

        const data = {
            id: taskDetails.id,
            priority: priorityPayload.priority,
            notify: priorityPayload.notify
        };

        updateTask({ data, activity });
    };


    const updateAssignee = (assigneePayload) => {
        const activity = {
            taskId: taskDetails.id,
            activityType: "assignment_change", 
            oldValue: taskDetails.assignedTo,
            newValue: assigneePayload.assignedTo,
            remark: assigneePayload.remark || null
        };

        const data = {
            id: taskDetails.id,
            assignedTo: assigneePayload.assignedTo,
            notify: assigneePayload.notify
        };

        updateTask({ data, activity });
    };


    const updateRemark = (remarkPayload) => {
        const activity = {
            taskId: taskDetails.id,
            activityType: "remark_update", 
            oldValue: null, // No old value for a new comment
            newValue: null, 
            remark: remarkPayload.remark
        };

        // We send the ID so the backend knows which task this belongs to,
        // even if we aren't changing specific task columns.
        const data = {
            id: taskDetails.id,
            notify: remarkPayload.notify
        };

        updateTask({ data, activity });
    };


    const updateSchedule = (payload) => {
        const oldVal = `${taskDetails.startDate} ${taskDetails.startTime} to ${taskDetails.endDate} ${taskDetails.endTime}`;
        const newVal = `${payload.startDate} ${payload.startTime} to ${payload.endDate} ${payload.endTime}`;

        const activity = {
            taskId: taskDetails.id,
            activityType: "schedule_change", 
            oldValue: oldVal,
            newValue: newVal,
            remark: payload.remark || null
        };

        const data = {
            id: taskDetails.id,
            startDate: payload.startDate,
            startTime: payload.startTime,
            endDate: payload.endDate,
            endTime: payload.endTime,
            notify: payload.notify
        };

        updateTask({ data, activity });
    };

    const updatePunchLocation = (payload) => {
        // Generate a Google Maps link as part of the activity data or newValue
        const mapUrl = payload.lat ? `https://www.google.com/maps?q=${payload.lat},${payload.lng}` : null;

        const activity = {
            taskId: taskDetails.id,
            activityType: "location_punch", 
            oldValue: null,
            // Saving the coordinates as a JSON string for the log
            newValue: JSON.stringify({ lat: payload.lat, lng: payload.lng, url: mapUrl }),
            remark: payload.remark
        };

        const data = {
            id: taskDetails.id,
            notify: payload.notify
            // If you have lat/lng columns on the task table, you can update them here too
        };

        updateTask({ data, activity });
    };


    const updateBasicInfo = (payload) => {
        // 1. Identify what actually changed and capture specific old/new values
        let changes = [];
        let oldState = {};
        let newState = {};

        if (payload.title !== taskDetails.title) {
            changes.push("Title");
            oldState.title = taskDetails.title;
            newState.title = payload.title;
        }

        if (payload.calendarType !== taskDetails.calendarType) {
            changes.push("Type");
            oldState.calendarType = taskDetails.calendarType;
            newState.calendarType = payload.calendarType;
        }

        if (payload.details !== taskDetails.details) {
            changes.push("Details");
            oldState.details = taskDetails.details;
            newState.details = payload.details;
        }

        // If nothing actually changed, don't trigger an update
        if (changes.length === 0 && !payload.remark) return;

        // 2. Build the activity log
        const activity = {
            taskId: taskDetails.id,
            activityType: "info_update", 
            // Convert objects to JSON strings so they can be stored in a single text/JSON column
            oldValue: JSON.stringify(oldState),
            newValue: JSON.stringify(newState),
            // Remark logic: uses user remark if provided, otherwise a summary of changed fields
            remark: payload.remark || `Fields updated: ${changes.join(", ")}`
        };

        // 3. Build the data update for the Task table
        const data = {
            id: taskDetails.id,
            title: payload.title,
            details: payload.details,
            calendarType: payload.calendarType,
            notify: payload.notify
        };

        updateTask({ data, activity });
    };


    // Contributor
    const addContributorCallBack = (contributorPayload) => {
        const activity = {
            taskId: taskDetails.id,
            activityType: "contributor_add", 
            oldValue: null,
            newValue: contributorPayload.contributorId,
            remark: contributorPayload.remark || null
        };

        const data = {
            id: taskDetails.id,
            notify: contributorPayload.notify
        };
        updateTask({ data, activity });
    };

    const removeContributorCallBack = (contributorPayload) => {
        const activity = {
            taskId: taskDetails.id,
            activityType: "contributor_remove", 
            oldValue: null,
            newValue: contributorPayload.contributorId,
        };

        const data = {
            id: taskDetails.id,
            notify: contributorPayload.notify
        };
        updateTask({ data, activity });
    };


    const archiveTask = (id) =>{
        setSavingStatus(true);
        taskServices.remove(id).then(res=>{
            AppAlert("Task Archived");
            callBack();
        }).catch(error=>{   
            AppAlert(error.msg,"error")
        }).finally(()=>{
            setSavingStatus(false);
        })
    }

    const restore = (id) =>{
        setSavingStatus(true);
        taskServices.restore(id).then(res=>{
            AppAlert("Task Restored");
            callBack();
        }).catch(error=>{   
            AppAlert(error.msg,"error")
        }).finally(()=>{
            setSavingStatus(false);
        })
    }

    return (
        taskDetails &&
        <Drawer 
                open={taskDetails!==null} 
                onClose={()=>setTaskDetails(null)}  
                anchor='right'
                
                PaperProps={{
                    sx: { width: { sm: '100%', md: '50%', marginTop:"75px" } } 
                }} 
            >
                <div className='card border-0'>
                    <div className='card-header'>
                        <span className='float-start'>
                            {taskServices.typeIcons[taskDetails.calendarType]} {taskDetails.title} 
                            <sapn className="text-muted small"> - {getDDDataById(userDD, taskDetails.userId)?.firstName}</sapn>
                            <TaskBasicInfoUpdate 
                                title={taskDetails.title} 
                                details={taskDetails.details} 
                                calendarType={taskDetails.calendarType}
                                callBack={isCreator ? updateBasicInfo : null} 
                            />
                            {(isCreator || authUser.role==1) && taskDetails.deleted_at === null &&
                                <ConfirmDialog id={taskDetails.id} confirm={archiveTask}>Are you sure you want to archive this task</ConfirmDialog>
                            }
                            {(isCreator || authUser.role ===1) && taskDetails.deleted_at !== null &&
                                <ConfirmDialog id={taskDetails.id} confirm={restore}>Are you sure You want to restore this task</ConfirmDialog>
                            }
                        </span>
                        <span className='float-end'>
                            <Tooltip title="Close"><IconButton onClick={()=>setTaskDetails(null)} style={{padding:"0px"}}><Close/></IconButton></Tooltip>
                        </span>
                    </div>
                    <div className='card-body'>
                        {saving && 
                            <div className='text-center'><CircularProgress/> </div>
                        }
                        {!saving &&
                            <div className='row'>
                                <div className='col-md-4'>
                                    <TaskProgressGauge value={taskDetails.progress} width={80} height={80} callback={ (isCreator || isAssignee) ? updateProgress :null} />
                                </div>

                                <div className='col-md-8'>
                                    <table className='table' >
                                        <tbody>
                                            <tr>
                                                <td><TaskStatusBadge value={taskDetails.taskStatus} callBack={ (isCreator || isAssignee) ? updateStatus : null} /></td>
                                                <td><PriorityBadge value={taskDetails.priority} callBack={isCreator ? updatePriority: null} /></td>
                                            </tr>
                                            <tr>
                                                <td><PunchLocationBadge callBack={updatePunchLocation} /></td>        
                                                <td><TaskRemarkUpdate callBack={updateRemark} /></td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            
                                <div className='col-md-12'>
                                    <span className='text-secondary'><InfoRounded/> DESCRIPTION</span>
                                    <div className='card bg-light p-2 my-2'>
                                        <div style={{ whiteSpace: 'pre-line' }}>{taskDetails.details === "" ? "No details provided for this task" : taskDetails.details}</div>
                                    </div>
                                </div>
                                
                                <hr className='my-3' />
                                <div className='col-md-12 my-1'>
                                    <span className='text-secondary'><Assignment/> ASSIGNMENT & SCHEDULE</span>
                                </div>

                                
                                <div className='col-md-6'>
                                    <div className='card p-2' style={{minHeight:80}}>
                                        <div className='text-secondary'>Assigned To</div>
                                        <div><AssigneeBadge value={taskDetails.assignedTo} callBack={ isCreator ?  updateAssignee : null} /></div>
                                    </div>
                                </div>
                                <div className='col-md-6'>
                                    <div className='card p-2' style={{minHeight:80}}>
                                        <div className='text-secondary'>Deadline / Schedule</div>
                                        <div>
                                            <TaskScheduleBadge 
                                                startDate={taskDetails.startDate}
                                                startTime={taskDetails.startTime}
                                                endDate={taskDetails.endDate}
                                                endTime={taskDetails.endTime}
                                                callBack={isCreator ? updateSchedule :null} 
                                            />
                                        </div>
                                    </div>                              
                                </div>
                                <div className='col-md-12 mt-2'>
                                    <div className='card p-2'>
                                        <div className='text-secondary'>Contributor</div>
                                        {(isCreator || isAssignee) ? 
                                            <ContributorBadge taskId={taskDetails.id} addContributorCallBack={addContributorCallBack} removeContributorCallBack={removeContributorCallBack} />
                                            :
                                            <ContributorBadge taskId={taskDetails.id}  />
                                        }

                                    </div>
                                </div>

                                <div className='col-md-12'>
                                    
                                    <hr/>
                                    <div className='mt-1'>
                                        <TaskActivityList taskId={taskDetails.id}/>
                                    </div>
                                </div>
                            </div>
                        }
                    </div>
                    
                    
                </div>
                
                
            </Drawer>
        )
}

export default TaskDetailsView